import * as React from "react";
import { X } from "lucide-react";
import type { UploadImage } from "@shared/types/product";
import { useTranslation } from "react-i18next";

import { cn } from "@bcc/lib/utils";
import { Button } from "@bcc/components/ui/button";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { setFileUploadThumbnailTestId } from "./file-upload.utils";

interface ThumbnailProps {
  image: UploadImage;
  isSelected: boolean;
  onSelect: (image: UploadImage, checked: boolean) => void;
  onDelete: (image: UploadImage) => void;
  selectType: "single" | "multiple" | "no-select";
  deleteEnabled: boolean;
  testId?: string;
  className?: string;
}

function Thumbnail({ image, isSelected, onSelect, onDelete, selectType, deleteEnabled, testId, className }: ThumbnailProps) {
  const { t } = useTranslation();

  // Compute image URL based on type
  const imageUrl = React.useMemo(() => {
    if (image.type === "file" && image.file) {
      return image.file.preview;
    }
    if (image.type === "url") {
      return image.thumbnailUrl ?? image.url;
    }
    return "";
  }, [image]);

  // Compute alt text
  const altText = React.useMemo(() => {
    if (image.type === "file" && image.file) {
      return image.file.name;
    }
    if (image.type === "url") {
      try {
        const filename = new URL(image.url).pathname.split("/").pop();
        if (filename) return filename;
      } catch {
        // fall through to default
      }
    }
    return t("components.fileUpload.thumbnail.defaultAlt");
  }, [image, t]);

  // Handle checkbox change (convert indeterminate to boolean)
  const handleCheckedChange = React.useCallback(
    (checked: boolean | "indeterminate") => {
      if (typeof checked === "boolean") {
        onSelect(image, checked);
      }
    },
    [image, onSelect]
  );

  // Handle delete click
  const handleDelete = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(image);
    },
    [image, onDelete]
  );

  // Generate test ID
  const finalTestId = testId ?? setFileUploadThumbnailTestId("default");

  return (
    <div
      data-testid={finalTestId}
      className={cn("relative aspect-square overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow-md", className)}
    >
      {/* Image */}
      <img src={imageUrl} alt={altText} className="h-full w-full object-cover" data-testid={`${finalTestId}-image`} />

      {/* Selection checkbox */}
      {selectType !== "no-select" && (
        <div className="absolute bottom-2 right-2 rounded bg-background/80 p-1 backdrop-blur-sm">
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckedChange}
            aria-label={t("components.fileUpload.thumbnail.selectImage")}
            testId={`${finalTestId}-checkbox`}
          />
        </div>
      )}

      {/* Delete button */}
      {deleteEnabled && (
        <Button
          variant="destructive"
          size="icon-sm"
          type="button"
          onClick={handleDelete}
          aria-label={t("components.fileUpload.thumbnail.removeImage")}
          testId={`${finalTestId}-delete`}
          className="absolute right-2 top-2 bg-destructive/80 backdrop-blur-sm hover:bg-destructive"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export { Thumbnail };
export type { ThumbnailProps };
